/**
 * Renesia thumbnail strip navigation.
 * Scrolls the thumbnail rail by roughly one viewport per press and shows an
 * arrow only when there is something to scroll to in that direction.
 *
 * The rail's own box rarely changes size - it is the CONTENT that grows when a
 * product has more images, or when variant switching swaps the media set - so
 * we watch the inner track and the child list as well as the rail itself.
 */
class RenesiaThumbNav extends HTMLElement {
  connectedCallback() {
    this.rail = this.querySelector('.slideshow-controls__thumbnails-container');
    this.track = this.querySelector('.slideshow-controls__thumbnails');
    this.prev = this.querySelector('[data-thumb-prev]');
    this.next = this.querySelector('[data-thumb-next]');
    if (!this.rail || !this.prev || !this.next) return;

    this.sync = this.sync.bind(this);

    this.prev.addEventListener('click', () => this.step(-1));
    this.next.addEventListener('click', () => this.step(1));
    this.rail.addEventListener('scroll', this.sync, { passive: true });
    window.addEventListener('resize', this.sync, { passive: true });

    // size changes: the rail box and the track that actually grows
    this.resizeObserver = new ResizeObserver(this.sync);
    this.resizeObserver.observe(this.rail);
    if (this.track) this.resizeObserver.observe(this.track);

    // content changes: thumbnails added or removed (variant media swaps)
    if (this.track) {
      this.mutationObserver = new MutationObserver(this.sync);
      this.mutationObserver.observe(this.track, { childList: true, subtree: true });
    }

    // images decode after first paint and can change the track width
    this.querySelectorAll('img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', this.sync, { once: true });
    });

    this.sync();
    requestAnimationFrame(this.sync);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    window.removeEventListener('resize', this.sync);
  }

  step(direction) {
    this.rail.scrollBy({ left: direction * this.rail.clientWidth * 0.8, behavior: 'smooth' });
  }

  sync() {
    const max = this.rail.scrollWidth - this.rail.clientWidth;
    const overflowing = max > 4;
    const x = this.rail.scrollLeft;
    this.prev.hidden = !overflowing || x <= 2;
    this.next.hidden = !overflowing || x >= max - 2;
    this.toggleAttribute('data-scrollable', overflowing);
  }
}

if (!customElements.get('renesia-thumb-nav')) {
  customElements.define('renesia-thumb-nav', RenesiaThumbNav);
}
